import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowUpRight, Banknote, CalendarIcon, CircleUserRound, PlusIcon } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { LoadingBlockFullPage } from '../../../components/loading/loading-block-full-page.tsx';
import { CurrencyOrEmpty } from '../../../components/typography/currency-or-empty.tsx';
import { Button } from '../../../components/ui/button.tsx';
import {
    DrawerContainer,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
} from '../../../components/ui/drawer.tsx';
import { LinkText } from '../../../components/ui/link-text.tsx';
import { Text } from '../../../components/ui/text.tsx';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { formatLongDate } from '../../../lib/date-helpers.ts';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { roundAbsToHundred } from '../../../lib/round-abs-to-hundred.ts';
import { generateSearchParamsForCustomerInsightsNormalisedCategory } from '../../../lib/search-params-helpers.ts';
import type { InsInsightOutcomeDTO } from '../../../server/dto/ins-insight-outcome-dto.ts';
import { richTextSchema } from '../../../server/types/common-types.ts';
import { SearchEntity } from '../../../server/types/search-types.ts';
import { api } from '../../../utils/api.ts';
import { useCustomerBasis } from '../../customer-detail/hooks/use-customer-basis.ts';
import { DialogForm } from '../../dialog/dialog-form.tsx';
import { Badge } from '../../list-views/components/badge.tsx';
import { RichTextInput } from '../../rich-text/components/rich-text-input.tsx';
import { useShowCreateTaskDialog } from '../../tasks/hooks/use-show-create-task-dialog.ts';
import { getInsightName } from '../lib/insight-name.ts';
import { getInsightOutcomeStatusColor, useInsightOutcomeResponseLabel } from '../lib/insight-outcome.ts';

const formSchema = z.object({
    textRich: richTextSchema.nullable(),
});

type InsightOutcomeFormSchema = z.infer<typeof formSchema>;

export type InsightOutcomeDetailsProps = {
    outcomeId: string;
    onBack?: () => void;
    onClose?: () => void;
};

export const InsightOutcomeDetails = ({ outcomeId, onBack, onClose }: InsightOutcomeDetailsProps) => {
    const { data: outcome, isLoading } = api.platoUser.query.insightOutcomes.getInsightOutcomeById.useQuery(
        { outcomeId },
        { enabled: !!outcomeId },
    );

    if (isLoading || !outcome) {
        return <LoadingBlockFullPage className="mt-64" />;
    }

    return <InsightOutcomeDetailsContent outcome={outcome} onBack={onBack} onClose={onClose} />;
};

type ContentProps = {
    outcome: InsInsightOutcomeDTO;
    onBack?: () => void;
    onClose?: () => void;
};

const InsightOutcomeDetailsContent = ({ outcome, onBack, onClose }: ContentProps) => {
    const { t, locale } = useLocale('insights');
    const { toast } = useToast();
    const apiUtils = api.useUtils();

    const customerId = outcome.insight.customer_id;
    const responseLabel = useInsightOutcomeResponseLabel(outcome.response);
    const insightTypeName = getInsightName(t, outcome.insight);
    const { data: customerBasics } = useCustomerBasis({ customerId });
    const showCreateTaskDialog = useShowCreateTaskDialog();

    const handleCreateTask = (): void => {
        showCreateTaskDialog({
            description: `${insightTypeName}: ${outcome.category.name}\n`,
            links: [
                {
                    modelId: customerId,
                    modelType: SearchEntity.Customer,
                    title: customerBasics?.customerName ?? '',
                },
            ],
            source: 'sales_insight',
        });
    };

    const isWebCategory = outcome.insight.actionable_entity_type === 'WEB_CATEGORY';
    const showDetailsHref = isWebCategory
        ? `/customers/${customerId}/insights?${generateSearchParamsForCustomerInsightsNormalisedCategory(
              outcome.insight.actionable_entity_id,
              outcome.category.name,
          )}`
        : undefined;

    const {
        handleSubmit,
        control,
        formState: { isDirty, isValid },
    } = useForm<InsightOutcomeFormSchema>({
        defaultValues: {
            textRich: outcome.textRich ?? null,
        },
        resolver: zodResolver(formSchema),
        mode: 'onChange',
    });

    const { mutate: submitInsightOutcome, isPending: isSubmitting } =
        api.platoUser.command.insights.postInsightActions.submitInsightOutcome.useMutation({
            onSuccess: () => {
                void apiUtils.platoUser.query.customerActivity.getFeedList.withFilters.invalidate();
                void apiUtils.platoUser.query.insightOutcomes.getInsightOutcomeById.invalidate({
                    outcomeId: outcome.id,
                });

                toast({
                    icon: 'success',
                    title: t('edit_outcome_toast__success__title'),
                    description: t('edit_outcome_toast__success__body'),
                });

                onClose?.();
            },
        });

    const handleFormSubmit = (data: InsightOutcomeFormSchema): void => {
        submitInsightOutcome({
            insightId: outcome.insight.id,
            status: outcome.status,
            response: outcome.response,
            textRich: data.textRich ?? undefined,
        });
    };

    return (
        <DrawerContainer>
            <DrawerHeader
                onBack={onBack}
                onClose={onClose}
                title={insightTypeName}
                actions={
                    <Button variant="subtle" onClick={handleCreateTask} icon={PlusIcon}>
                        {t('action__create_task')}
                    </Button>
                }
            >
                {responseLabel && (
                    <Badge color={getInsightOutcomeStatusColor(outcome.response)}>{responseLabel}</Badge>
                )}
            </DrawerHeader>
            <DrawerContent>
                <Controller
                    name="textRich"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <RichTextInput
                            trackingContext={{ source: 'sales_insight_outcome' }}
                            placeholder={t('edit_outcome_modal__placeholder')}
                            value={value ?? undefined}
                            onChange={onChange}
                        />
                    )}
                />

                <DialogForm.ItemList className="rounded-lg bg-white py-2 px-3 shadow-xs" grid>
                    <DialogForm.Item className="items-start">
                        <DialogForm.Label icon={ArrowUpRight}>
                            {t('edit_outcome_modal__related_to')}
                        </DialogForm.Label>
                        <DialogForm.Body className="flex flex-col gap-1 py-1.5 px-2">
                            <Text color="neutral-950">{outcome.category.name}</Text>
                            {showDetailsHref && (
                                <LinkText href={showDetailsHref} className="text-sm">
                                    {t('show_details', { ns: 'common' })}
                                </LinkText>
                            )}
                        </DialogForm.Body>
                    </DialogForm.Item>

                    <DialogForm.Item>
                        <DialogForm.Label icon={Banknote}>{t('edit_outcome_modal__value')}</DialogForm.Label>
                        <DialogForm.Body className="items-center px-2 [font-variant-numeric:lining-nums_tabular-nums]">
                            <Text color="neutral-950">
                                <CurrencyOrEmpty>
                                    {outcome.insight.absolute_number
                                        ? roundAbsToHundred(outcome.insight.absolute_number)
                                        : null}
                                </CurrencyOrEmpty>{' '}
                                {t('insight_outcome__est_value')}
                            </Text>
                        </DialogForm.Body>
                    </DialogForm.Item>

                    <DialogForm.Item>
                        <DialogForm.Label icon={CalendarIcon}>
                            {t('edit_outcome_modal__date')}
                        </DialogForm.Label>
                        <DialogForm.Body className="items-center px-2">
                            <Text color="neutral-950">{formatLongDate(outcome.createdAt, locale)}</Text>
                        </DialogForm.Body>
                    </DialogForm.Item>

                    <DialogForm.Item>
                        <DialogForm.Label icon={CircleUserRound}>
                            {t('edit_outcome_modal__resolved_by')}
                        </DialogForm.Label>
                        <DialogForm.Body className="items-center px-2">
                            <Text color="neutral-950">{outcome.createdBy.fullName}</Text>
                        </DialogForm.Body>
                    </DialogForm.Item>
                </DialogForm.ItemList>
            </DrawerContent>
            <DrawerFooter>
                <div>
                    <Button
                        variant="primary"
                        type="button"
                        onClick={handleSubmit(handleFormSubmit)}
                        disabled={!isDirty || !isValid || isSubmitting}
                        isLoading={isSubmitting}
                    >
                        {t('edit_outcome_modal__save')}
                    </Button>
                </div>
            </DrawerFooter>
        </DrawerContainer>
    );
};
